import { createContext, FC, useCallback, useEffect, useMemo, useState } from 'react';

// project import
import useAuth from 'hooks/useAuth';
import FamigliaDiInterventoService from 'services/FamigliaDiInterventoService';
import { FamigliaDiIntervento } from 'models/FamigliaDiIntervento';
import LottoService from 'services/LottoService';
import { Lotto } from 'models/results/lotto';

// initial state
type PermissionContextType = {
    canAccessPlan: (planId?: string, lottoId?: string) => boolean;
    canAccess: (matricola?: string) => boolean;
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const PermissionContext = createContext<PermissionContextType | null>(null);

const PermissionProvider: FC = ({ children }) => {
    const { user } = useAuth();

    const [planItems, setPlanItems] = useState<FamigliaDiIntervento[]>([]);
    const [lottoItems, setLottoItems] = useState<Lotto[]>([]);

    const famigliaDiInterventoService = new FamigliaDiInterventoService();
    const lottoService = new LottoService();

    const checkReferent = useCallback(
        (matricola: string, planId?: string, lottoId?: string) => {
            const plan = planItems.find((dp) => dp.id === planId);
            if (!plan) return false;

            const lotto = lottoItems.find((dp) => dp.id === lottoId);

            return (
                plan.users?.some((user) => user.matricola.includes(matricola)) ||
                (lotto ? lotto.users?.some((user) => user.matricola.includes(matricola)) : false) ||
                false
            );
        },
        [planItems, lottoItems]
    );

    const canAccessPlan = useMemo(() => {
        if (!user) return () => false;
        if (user.isSystemAdmin) return () => true;

        const matricola = user.userName.split('@')[0];

        return (planId?: string, lottoId?: string) => checkReferent(matricola, planId, lottoId);
    }, [user, checkReferent]);

    const canAccess = useMemo(() => {
        if (!user) return () => false;
        if (user.isSystemAdmin) return () => true;

        const matricola = user.userName.split('@')[0];

        return (userMatricola?: string) => userMatricola === matricola;
    }, [user, checkReferent]);

    useEffect(() => {
        const fetchPlans = async () => {
            if (!user) return;

            try {
                const [plans, lotti] = await Promise.all([
                    famigliaDiInterventoService.getFamiglieDiInterventoItems(user.tenantId),
                    lottoService.getLottoItems(user.tenantId, null, null)
                ]);
                setPlanItems(plans);
                setLottoItems(lotti);
            } catch (error) {
                console.error(error);
            }
        };

        fetchPlans();
    }, [user]);

    return <PermissionContext.Provider value={{ canAccessPlan, canAccess }}>{children}</PermissionContext.Provider>;
};

export { PermissionProvider, PermissionContext };
